import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtTons, fmtMoney, fmtDate, monthLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileText, ChevronDown, Pencil } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fy, setFy] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);
  const [monthSales, setMonthSales] = useState(null);
  const [edit, setEdit] = useState(null);

  const saveLine = async () => {
    try {
      await api.put(`/sales/${edit.id}`, { sale_date: edit.sale_date, tons: Number(edit.tons), vehicle_no: edit.vehicle_no });
      toast.success("Sale updated");
      const m = openMonth; setEdit(null);
      await load(fy);
      const { data } = await api.get(`/customers/${id}/month-sales`, { params: { month: m } });
      setMonthSales(data);
    } catch (e) { toast.error("Update failed"); }
  };

  const load = useCallback(async (selFy) => {
    const params = selFy ? { fy: selFy } : {};
    const { data } = await api.get(`/customers/${id}/history`, { params });
    setData(data);
    setFy(data.fy);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openMonthSales = async (month) => {
    if (openMonth === month) { setOpenMonth(null); setMonthSales(null); return; }
    const { data } = await api.get(`/customers/${id}/month-sales`, { params: { month } });
    setOpenMonth(month);
    setMonthSales(data);
  };

  if (!data) return <p className="text-slate-400">Loading…</p>;
  const c = data.customer;
  const totalTons = data.rows.reduce((s, r) => s + (r.total_tons || 0), 0);
  const totalAmount = data.rows.reduce((s, r) => s + (r.total_amount || 0), 0);

  return (
    <div>
      <button onClick={() => navigate("/admin/customers")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-4" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Back to Customers
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-chivo font-black text-3xl text-slate-900" data-testid="customer-name-heading">{c.name}</h1>
          <p className="text-slate-500 mt-1">{c.phone || "No phone"}{c.address ? ` · ${c.address}` : ""}</p>
        </div>
        <div className="w-52">
          <Label className="text-xs">Financial Year</Label>
          <Select value={fy || ""} onValueChange={(v) => { setOpenMonth(null); load(v); }}>
            <SelectTrigger data-testid="customer-fy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(data.fys.length ? data.fys : [data.fy]).map((f) => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full text-sm" data-testid="customer-history-table">
          <thead className="bg-slate-50 text-slate-500 text-left border-b border-slate-200">
            <tr>
              <th className="p-3 font-semibold">Month</th>
              <th className="p-3 text-right font-semibold">Total Tons</th>
              <th className="p-3 text-right font-semibold">Rate/Ton</th>
              <th className="p-3 text-right font-semibold">Total Amount</th>
              <th className="p-3 text-right font-semibold">Bill</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <Fragment key={r.month}>
                <tr
                  className={`border-t border-slate-100 cursor-pointer transition-colors ${r.total_tons > 0 ? "hover:bg-sky-50/50" : "opacity-50"}`}
                  onClick={() => r.total_tons > 0 && openMonthSales(r.month)}
                  data-testid={`history-row-${r.month}`}>
                  <td className="p-3 font-medium text-slate-900 flex items-center gap-1">
                    {r.total_tons > 0 && <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openMonth === r.month ? "rotate-180" : ""}`} />}
                    {monthLabel(r.month)}
                  </td>
                  <td className="p-3 text-right tabular">{r.total_tons ? fmtTons(r.total_tons) : "—"}</td>
                  <td className="p-3 text-right tabular">{r.rate != null ? fmtMoney(r.rate) : <span className="text-slate-300">not billed</span>}</td>
                  <td className="p-3 text-right tabular font-semibold">{r.total_amount != null ? fmtMoney(r.total_amount) : "—"}</td>
                  <td className="p-3 text-right">
                    {r.has_bill && (
                      <Button size="sm" variant="outline" data-testid={`open-bill-${r.month}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/billing?customer=${encodeURIComponent(c.name)}&month=${r.month}`); }}>
                        <FileText className="h-3.5 w-3.5 mr-1" /> Open
                      </Button>
                    )}
                  </td>
                </tr>
                {openMonth === r.month && monthSales && (
                  <tr key={`${r.month}-detail`} className="bg-slate-50/70">
                    <td colSpan={5} className="p-4">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sales in {monthLabel(r.month)}</div>
                      <table className="w-full text-sm bg-white border border-slate-200 rounded">
                        <thead className="text-slate-500 text-left border-b border-slate-200">
                          <tr><th className="p-2">Date</th><th className="p-2 text-right">Quantity</th><th className="p-2">Vehicle No.</th><th className="p-2 text-right">Amount</th><th className="p-2"></th></tr>
                        </thead>
                        <tbody>
                          {monthSales.lines.map((l, i) => (
                            <tr key={i} className="border-b border-slate-100 last:border-0">
                              <td className="p-2">{fmtDate(l.sale_date)}</td>
                              <td className="p-2 text-right tabular">{fmtTons(l.tons)} tons</td>
                              <td className="p-2">{l.vehicle_no || "—"}</td>
                              <td className="p-2 text-right tabular">{l.amount != null ? fmtMoney(l.amount) : <span className="text-slate-300">rate not set</span>}</td>
                              <td className="p-2 text-right">
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-sky-600"
                                  data-testid={`edit-sale-${l.id}`}
                                  onClick={() => setEdit({ id: l.id, sale_date: l.sale_date, tons: l.tons, vehicle_no: l.vehicle_no || "" })}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {monthSales.lines.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-slate-400">No sales.</td></tr>}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-900 text-white font-bold">
              <td className="p-3">FY {fy} Total</td>
              <td className="p-3 text-right tabular">{fmtTons(totalTons)} t</td>
              <td></td>
              <td className="p-3 text-right tabular">{fmtMoney(totalAmount)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">Amounts appear only for months where a Monthly Bill has been saved with a rate.</p>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-sale-dialog">
          <DialogHeader><DialogTitle>Edit Sale</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-4 py-2">
              <div><Label>Date</Label><Input type="date" data-testid="edit-sale-date" value={edit.sale_date} onChange={(e) => setEdit({ ...edit, sale_date: e.target.value })} /></div>
              <div><Label>Quantity (tons)</Label><Input type="number" step="0.001" data-testid="edit-sale-tons" value={edit.tons} onChange={(e) => setEdit({ ...edit, tons: e.target.value })} /></div>
              <div><Label>Vehicle No.</Label><Input data-testid="edit-sale-vehicle" value={edit.vehicle_no} onChange={(e) => setEdit({ ...edit, vehicle_no: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} data-testid="edit-sale-cancel">Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={saveLine} data-testid="edit-sale-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
